import { UseQueryResult } from "@tanstack/react-query";
import { useEffect, useRef } from "react";

interface useQuerySuccessEffectOptions<TData> {
  query: UseQueryResult<TData, Error>;
  onSuccess: (data: TData) => void;
}

/** Example Usage
    const query = useRfpCoverages({ rfpId });
    useQuerySuccessEffect({
        query,
        onSuccess: (data) => {
            // do something with data, e.g. fire analytics event
            // should use useCallback if passing a function that uses component state/props
        },
    });
*/
// useQuerySuccessEffect fires a callback once when query data becomes available
export function useQuerySuccessEffect<TData>({
  query,
  onSuccess,
}: useQuerySuccessEffectOptions<TData>): UseQueryResult<TData, Error> {
  const hasCalledOnSuccess = useRef(false);

  useEffect(() => {
    if (query.data && !hasCalledOnSuccess.current) {
      onSuccess(query.data);
      hasCalledOnSuccess.current = true;
    }
  }, [query.data, onSuccess]);

  return query;
}
